import { RotateCcw, ShieldCheck } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import type { MenuItem } from '../../../../components/Menu';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { ApiError } from '../../../../lib/apiError';
import { CreateRoleModal } from '../components/CreateRoleModal';
import { EditRoleModal } from '../components/EditRoleModal';
import { RolePermissionsModal } from '../components/RolePermissionsModal';
import { rolesApi } from '../rolesApi';
import type { RoleListItem } from '../roleTypes';
import {
  roleDeleteDisabledReason,
  roleEditDisabledReason,
  rolePermissionsDisabledReason,
} from '../utils/roleState';

/**
 * Roles list — same shell, different columns. Reference §3.
 *
 * The anchor opens the **profile**, not the edit form: every index page is partnered with one,
 * and the profile is where "what does this role actually grant" can be read and linked to.
 */
export function RolesIndexPage() {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<RoleListItem | null>(null);
  const [permissionsFor, setPermissionsFor] = useState<RoleListItem | null>(null);
  const [confirming, setConfirming] = useState<{ kind: 'deactivate' | 'activate'; role: RoleListItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRoles = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      rolesApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (role: RoleListItem): MenuItem[] => [
      {
        // A row-menu entry rather than `onEdit`, because a system role cannot be renamed and
        // the shell's own Edit has no disabled reason to say so.
        key: `edit-${role.id}`,
        label: 'Edit',
        permission: 'roles.update',
        disabledReason: roleEditDisabledReason(role),
        onClick: () => setEditing(role),
      },
      {
        key: `permissions-${role.id}`,
        label: 'Permissions',
        icon: <ShieldCheck className="h-4 w-4" aria-hidden="true" />,
        permission: 'roles.assign-permissions',
        disabledReason: rolePermissionsDisabledReason(role),
        onClick: () => setPermissionsFor(role),
      },
      {
        key: `activate-${role.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'roles.delete',
        disabledReason: role.isActive ? 'This role is already active.' : null,
        onClick: () => setConfirming({ kind: 'activate', role }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'deactivate') await rolesApi.deactivate(confirming.role.id);
      else await rolesApi.activate(confirming.role.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<RoleListItem>
        module="roles"
        breadcrumb={['Administration', 'Roles']}
        columns={COLUMNS}
        facets={[{ key: 'status', label: 'Status' }]}
        auditEntityTypes={['ApplicationRole', 'RolePermission']}
        rowKey={(role) => role.id}
        fetch={fetchRoles}
        reloadKey={reloadKey}
        searchPlaceholder="Search roles"
        emptyMessage="No roles yet."
        onCreate={() => setCreating(true)}
        createPermission="roles.create"
        // The read key, not the write one — opening a record is not changing it.
        onRowOpen={(role) => navigate(`/roles/${role.id}`)}
        openPermission="roles.read"
        rowActions={rowActions}
        onDelete={(role) => setConfirming({ kind: 'deactivate', role })}
        deletePermission="roles.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={roleDeleteDisabledReason}
        onClearData={() => rolesApi.clear()}
        clearDataPermission="roles.clear-data"
        otherInfo={(role) => role}
      />

      <CreateRoleModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={refresh}
      />

      {editing ? (
        <EditRoleModal
          role={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            refresh();
          }}
        />
      ) : null}

      <RolePermissionsModal
        role={permissionsFor}
        onClose={() => setPermissionsFor(null)}
        onSaved={() => {
          setPermissionsFor(null);
          refresh();
        }}
      />

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'deactivate' ? 'Delete role' : 'Restore role'}
          danger={confirming.kind === 'deactivate'}
          requireConfirm={confirming.kind === 'deactivate'}
          requireCredential={confirming.kind === 'deactivate'}
          loading={running}
          id={`role-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'deactivate'
                  ? `"${confirming.role.name}" will stop granting its permissions. It can be restored later.`
                  : `"${confirming.role.name}" will grant its permissions again.`}
              </p>
              {confirming.kind === 'deactivate' ? (
                <p>Nothing is erased — a deleted role can be restored from this list.</p>
              ) : null}
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: ColumnDef<RoleListItem>[] = [
  {
    key: 'name',
    header: 'Role',
    width: 220,
    sortable: true,
    render: (role) => <TextCell value={role.name} />,
  },
  {
    key: 'description',
    header: 'Description',
    width: 320,
    flex: true,
    render: (role) => <TextCell value={role.description} />,
  },
  {
    key: 'members',
    header: 'Members',
    width: 110,
    align: 'right',
    // A count of ACCOUNTS — another module's data. The role's own columns stay ungated.
    permission: 'users.read',
    render: (role) => <TextCell value={String(role.userCount)} mono numeric />,
  },
  {
    key: 'permissions',
    header: 'Permissions',
    width: 130,
    align: 'right',
    render: (role) => <TextCell value={String(role.permissionCount)} mono numeric />,
  },
  {
    key: 'type',
    header: 'Type',
    width: 120,
    render: (role) => <TextCell value={role.isSystem ? 'System' : 'Custom'} />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (role) =>
      role.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
